import React, { useContext } from 'react';
import cx from 'classnames';
import Context from '../context/ContextStorefront';
import NavNewsletter from './NavNewsletter';
import BlockContent from '@sanity/block-content-to-react';
import richText from '../serializers/richText';
import Link from './Link';
import find from 'lodash/find';
import reduce from 'lodash/reduce';
import { SwitchTransition, Transition } from 'react-transition-group';

const TRANSITION_DURATION = 400;

const TRANSITION_STYLES = {
    default: {
        transition: `opacity ${TRANSITION_DURATION}ms ease, transform ${TRANSITION_DURATION}ms ease`,
    },
    entering: {
        opacity: 0,
        transform: 'translateY(10px)',
    },
    entered: {
        opacity: 1,
        transform: 'translateY(0px)',
    },
    exiting: {
        opacity: 0,
        transform: 'translateY(10px)',
    },
    exited: {
        opacity: 0,
    },
};
const SLIDE_STYLES = {
    default: {
        transition: `opacity ${TRANSITION_DURATION}ms ease, transform ${TRANSITION_DURATION}ms ease`,
    },
    entering: {
        opacity: 0,
        transform: 'translateY(10px)',
    },
    entered: {
        opacity: 1,
        transform: 'translateY(0px)',
    },
    exiting: {
        opacity: 0,
        transform: 'translateY(10px)',
    },
    exited: {
        opacity: 0,
    },
};

const Header = ({
    enableBanner,
    banner,
    desktopMenu = [],
    mobileMenu = [],
}) => {
    const {
        checkout,
        isSearchOpen,
        setSearchOpen,
        isCartOpen,
        setCartOpen,
        isMenuOpen,
        setIsMenuOpen,
        isShopMenuActive,
        setIsShopMenuActive,
        hasSeenBanner,
        setHasSeenBanner,
    } = useContext(Context);

    const { lineItems = [] } = checkout;

    const cartCount = reduce(
        lineItems,
        (total, item) => {
            return total + item.quantity;
        },
        0
    );

    const bannerTicker = enableBanner && !hasSeenBanner && (
        <aside className="bg--black bg--black color--white mono--12 uppercase tc pt1 pb1 p0--lg banner pr df fdc jcc pr">
            <div className="grid-container contained">
                <BlockContent blocks={banner} serializers={richText} />
            </div>
            <button
                className="mono--11 mono--13--lg color--white pa right mr1 fit"
                onClick={() => setHasSeenBanner(true)}
            >
                x
            </button>
        </aside>
    );

    const dropDownMenu = find(desktopMenu, item => !!item.dropdownLinks) || [];

    const mobileNav = (
        <SwitchTransition>
            <Transition
                key={isShopMenuActive}
                mountOnEnter
                unmountOnExit
                appear
                timeout={TRANSITION_DURATION}
            >
                {status => (
                    <div
                        className="mobile-menu__container x pl2 pr2"
                        style={{
                            ...SLIDE_STYLES.default,
                            ...SLIDE_STYLES[status],
                        }}
                    >
                        {!isShopMenuActive && (
                            <ul
                                className="mobile-menu__nav"
                                style={{
                                    ...SLIDE_STYLES.default,
                                    ...SLIDE_STYLES[status],
                                }}
                            >
                                {mobileMenu.map((item, index) => {
                                    const {
                                        _key,
                                        title,
                                        dropdownLinks = [],
                                    } = item;
                                    if (!!dropdownLinks.length) {
                                        return (
                                            <li
                                                key={_key}
                                                className="pt2 pb2 x mobile-menu__shop-link-border"
                                            >
                                                <button
                                                    className="mono--15 uppercase"
                                                    onClick={() =>
                                                        setIsShopMenuActive(
                                                            true
                                                        )
                                                    }
                                                >
                                                    {title}
                                                </button>
                                            </li>
                                        );
                                    } else {
                                        return (
                                            <li
                                                key={_key}
                                                className="pt2 pb2 x mobile-menu__shop-link-border"
                                            >
                                                <Link
                                                    onClick={() =>
                                                        setIsMenuOpen(false)
                                                    }
                                                    className="mono--15 uppercase "
                                                    {...item}
                                                />
                                            </li>
                                        );
                                    }
                                })}
                                <li className="pt2 pb2 x mobile-menu__shop-link-border">
                                    <button
                                        onClick={() => {
                                            setSearchOpen(true);
                                            setIsMenuOpen(false);
                                        }}
                                        className="mono--15 uppercase "
                                    >
                                        Search
                                    </button>
                                </li>
                            </ul>
                        )}
                        {isShopMenuActive && (
                            <div className="mobile-menu__shop-links x">
                                <p className="color--black mono--15 uppercase mobile-menu__shop-link-border pt2 pb2">
                                    Shop Categories
                                </p>
                                <ul className="">
                                    {find(
                                        mobileMenu,
                                        item => !!item.dropdownLinks
                                    ).dropdownLinks.map(item => {
                                        return (
                                            <li key={item._key}>
                                                <Link
                                                    onClick={() => {
                                                        setIsShopMenuActive(
                                                            false
                                                        );
                                                        setIsMenuOpen(false);
                                                    }}
                                                    {...item}
                                                    className="button--pill mt2 dib"
                                                >
                                                    {item.linkTitle}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <button
                                    onClick={() => {
                                        setIsShopMenuActive(false);
                                    }}
                                    className="color--black mono--15 uppercase mobile-menu__shop-link-border pt2 pb2 mt2 x"
                                >
                                    Back to Menu
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Transition>
        </SwitchTransition>
    );

    const desktopNav = !!dropDownMenu.dropdownLinks && (
        <SwitchTransition>
            <Transition
                key={isMenuOpen}
                mountOnEnter
                unmountOnExit
                appear
                timeout={TRANSITION_DURATION}
            >
                {status => (
                    <div
                        style={{
                            ...TRANSITION_STYLES.default,
                            ...TRANSITION_STYLES[status],
                        }}
                        className={cx(
                            'desktop-shop-nav grid-container contained',
                            {
                                active: isShopMenuActive,
                            }
                        )}
                    >
                        <div className="desktop-shop__list">
                            <ul className="">
                                {dropDownMenu.dropdownLinks.map(item => {
                                    return (
                                        <li
                                            className="fit"
                                            key={item._key}
                                            onClick={() => {
                                                setIsMenuOpen(false);
                                                setIsShopMenuActive(false);
                                            }}
                                        >
                                            <Link
                                                title={item.title}
                                                url={`/collections/${item.url}`}
                                                className="serif--47 serif--80--lg link--underline-revert"
                                            >
                                                {item.linkTitle}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                )}
            </Transition>
        </SwitchTransition>
    );

    return (
        <React.Fragment>
            <header
                className={cx('header psy', {
                    'banner-open': enableBanner,
                })}
            >
                {bannerTicker}
                <section
                    className={cx('header__nav pr x psy top', {
                        'banner-open': enableBanner,
                        'hide-fade': isShopMenuActive,
                    })}
                >
                    <nav className="hide--lg df header__mobile x pr">
                        <button
                            className="mono--12 uppercase header__mobile-button "
                            onClick={() => {
                                setSearchOpen(false);
                                setIsMenuOpen(!isMenuOpen);
                            }}
                        >
                            {isMenuOpen ? 'Close' : 'Menu'}
                        </button>
                        <button
                            className="mono--12 uppercase header__mobile-button "
                            onClick={() => {
                                setSearchOpen(false);
                                setCartOpen(!isCartOpen);
                            }}
                        >
                            ({cartCount})
                        </button>
                    </nav>
                    <nav className="show--lg df jcb pt2 pb2 header__desktop-menu grid-container contained">
                        <Link
                            url="/"
                            className="uppercase mono--17 header__desktop-menu-item--home pr"
                            onClick={() => {
                                setSearchOpen(false);
                                setIsMenuOpen(false);
                                setIsShopMenuActive(false);
                            }}
                        >
                            F
                        </Link>
                        {desktopMenu.map(item => {
                            const { _key, title, dropdownLinks = [] } = item;
                            if (!!dropdownLinks.length) {
                                return (
                                    <button
                                        key={_key}
                                        className={cx(
                                            'mono--17 uppercase header__desktop-menu-item pr',
                                            {
                                                active: isShopMenuActive,
                                                'font--navy':
                                                    title.toLowerCase() ===
                                                    'shop',
                                            }
                                        )}
                                        onClick={() => {
                                            setSearchOpen(false);
                                            setIsShopMenuActive(
                                                !isShopMenuActive
                                            );
                                        }}
                                    >
                                        {title}
                                    </button>
                                );
                            } else {
                                return (
                                    <Link
                                        key={_key}
                                        className="mono--17 uppercase header__desktop-menu-item pr"
                                        {...item}
                                        activeClassName="active"
                                        onClick={() => {
                                            setSearchOpen(false);
                                            setIsMenuOpen(false);
                                            setIsShopMenuActive(false);
                                        }}
                                    />
                                );
                            }
                        })}
                        <button
                            className={cx(
                                'mono--17 uppercase header__desktop-menu-item pr',
                                {
                                    active: isSearchOpen,
                                }
                            )}
                            onClick={() => setSearchOpen(!isSearchOpen)}
                        >
                            Search
                        </button>
                        <button
                            className="mono--17 uppercase header__desktop-menu-item pr"
                            onClick={() => setCartOpen(!isCartOpen)}
                        >
                            Cart ({cartCount})
                        </button>
                    </nav>
                </section>
            </header>
            <nav
                className={cx('pf mobile-menu  df fdc jcc aic tc hide--lg', {
                    active: isMenuOpen,
                })}
            >
                {mobileNav}
                <NavNewsletter />
            </nav>

            <nav
                onClick={e => {
                    if (e.target.nodeName !== 'A') {
                        setIsShopMenuActive(false);
                    }
                }}
                className={cx('pf fill shop-menu  show--lg', {
                    active: isShopMenuActive,
                    pt12: !hasSeenBanner,
                    pt10: hasSeenBanner,
                })}
            >
                {desktopNav}
            </nav>
        </React.Fragment>
    );
};

export default Header;
